import { css } from 'aphrodite';
import React, { useEffect, useState } from 'react';

import { orgsApi, Org } from '@/features/org/api/orgs.api';
import { orgStyles as s } from '@/features/org/components/org.styles';
import SettingsLayout from '@/features/org/components/settings/settings-layout/SettingsLayout';
import { useOrgs } from '@/features/org/hooks/use-orgs/useOrgs';
import { useSlugCheck } from '@/features/org/hooks/use-slug-check/useSlugCheck';
import { useParams, useHistory } from '@/shared/hooks/use-router/react-router-hooks';

/**
 * /o/:slug/settings — rename or soft-delete the org.
 *
 * Soft-delete is allowed only when the org has zero live apps. The
 * backend returns 409 in that case; we surface the message.
 */
export default function OrgSettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const history = useHistory();
  const { switchOrg, refresh, deleteOrg, myRole } = useOrgs();

  const [org, setOrg] = useState<Org | null>(null);
  const [name, setName] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    switchOrg(slug);
  }, [slug, switchOrg]);

  useEffect(() => {
    orgsApi
      .bySlug(slug)
      .then((r) => {
        setOrg(r.org);
        setName(r.org.name);
        setNewSlug(r.org.slug);
      })
      .catch((e) => setErr(e.message || 'Failed to load org'));
  }, [slug]);

  const canManage = myRole === 'owner';

  // Live availability check for the slug input. Passing `org.id` as
  // excludeOrgId so "no change" reports as available — otherwise the
  // form would always look "taken by itself" until the user edits.
  const slugCheck = useSlugCheck(newSlug, org?.id);
  const slugUnchanged = !!org && newSlug === org.slug;
  const slugBlocksSave = !slugUnchanged && slugCheck.status !== 'available';

  const handleSave = async () => {
    if (!org) return;
    setBusy(true);
    setErr(null);
    try {
      const update: { name?: string; slug?: string } = {};
      if (name !== org.name) update.name = name;
      if (newSlug !== org.slug) update.slug = newSlug;
      if (!Object.keys(update).length) {
        setBusy(false);
        return;
      }
      const res = await orgsApi.update(slug, update);
      setOrg(res.org);
      await refresh();
      // Slug changed → URL is stale.
      if (res.org.slug !== slug) {
        history.replace(`/o/${res.org.slug}/settings`);
      }
    } catch (e: any) {
      setErr(e.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(`Delete "${org?.name}"? This is a soft-delete; you can restore it from the server.`)
    )
      return;
    try {
      await deleteOrg(slug);
      history.push('/dashboard');
    } catch (e: any) {
      setErr(e.message || 'Failed to delete org');
    }
  };

  return (
    <SettingsLayout>
      <div className={css(s.page)}>
        <div className={css(s.pageHeader)}>
          <div>
            <div className={css(s.pageTitle)}>Org settings</div>
            <div className={css(s.pageSubtitle)}>Manage this org's identity and lifecycle.</div>
          </div>
        </div>

        {err && <div className={css(s.errorBox)}>{err}</div>}

        {!org ? (
          <div className={css(s.empty)}>Loading…</div>
        ) : (
          <>
            <div className={css(s.field)}>
              <label className={css(s.label)}>Name</label>
              <input
                className={css(s.input)}
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canManage}
              />
            </div>
            <div className={css(s.field)}>
              <label className={css(s.label)}>Slug</label>
              <input
                className={css(s.input, s.inputMono)}
                value={newSlug}
                onChange={(e) => setNewSlug(e.target.value.toLowerCase())}
                pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
                disabled={!canManage}
              />
              <SlugStatusRow slug={newSlug} unchanged={slugUnchanged} state={slugCheck} />
            </div>

            {canManage && (
              <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
                <button
                  className={css(s.btn)}
                  onClick={handleSave}
                  disabled={busy || slugBlocksSave}
                >
                  {busy ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            )}

            <div style={{ marginTop: 48, paddingTop: 24, borderTop: '1px solid var(--gray-100)' }}>
              <div
                style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-900)', marginBottom: 4 }}
              >
                Danger zone
              </div>
              <div className={css(s.pageSubtitle)} style={{ marginBottom: 12 }}>
                Soft-delete this org. All members lose access; data is preserved on the server.
                Requires the org to be empty of apps first.
              </div>
              {canManage && (
                <button className={css(s.btnDanger)} onClick={handleDelete}>
                  Delete org
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </SettingsLayout>
  );
}

/**
 * Inline status row under the slug input. "unchanged" wins over the
 * check result so the no-edit case doesn't read as "available" (which
 * would imply the user has typed something).
 */
function SlugStatusRow({
  slug,
  unchanged,
  state,
}: {
  slug: string;
  unchanged: boolean;
  state: { status: string; message: string | null };
}) {
  let icon = '·';
  let color: string = 'var(--td-gray-500, #777)';
  let label = 'enter a slug';
  if (!slug) {
    label = 'enter a slug';
  } else if (unchanged) {
    icon = '·';
    label = 'current URL — no change';
  } else if (state.status === 'checking') {
    icon = '…';
    label = 'checking…';
  } else if (state.status === 'available') {
    icon = '✓';
    color = '#1B5E20';
    label = 'available';
  } else if (state.status === 'unavailable') {
    icon = '✗';
    color = '#7A0000';
    label = state.message || 'unavailable';
  }
  return (
    <div
      style={{
        marginTop: 6,
        fontSize: 11,
        color: 'var(--td-gray-500, #777)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <code style={{ fontFamily: 'var(--mono, monospace)' }}>/o/{slug || '…'}/dashboard</code>
      <span style={{ marginLeft: 'auto', color, fontWeight: 600 }}>
        {icon} {label}
      </span>
    </div>
  );
}
